<script setup lang="ts">
  import { onMounted, ref } from 'vue'
  import { Button } from '@/components/ui/button'
  import { useTranslation } from '@/composables/useTranslation'
  import { X } from 'lucide-vue-next'
  import FeedbackModal from '@/components/app/cases/feedbackModal.vue'
  import { FeatureEnum } from '@/types/generated/types.gen'

  defineProps<{
    mode?: string
    caseId?: string | number
  }>()

  const showFeedbackPopover = ref(false)
  const showFeedbackModal = ref(false)

  // Define translations
  const { t } = useTranslation({
    en: {
      feedbackTitle: 'How do you like the new design?',
      feedbackDescription:
        'Please share your feedback with us so we can make the new view even better for you.',
      feedbackButton: 'Give feedback',
      dismissButton: 'Dismiss'
    },
    de: {
      feedbackTitle: 'Wie finden Sie das neue Design?',
      feedbackDescription:
        'Bitte teilen Sie uns Ihr Feedback mit, damit wir die neue Ansicht noch besser für Sie gestalten können.',
      feedbackButton: 'Feedback geben',
      dismissButton: 'Verwerfen'
    }
  })

  onMounted(() => {
    // Check if the banner has been permanently dismissed
    const permanentlyDismissed = localStorage.getItem('feedbackPopoverPermanentlyDismissed')
    if (permanentlyDismissed === 'true') {
      return // Don't show if permanently dismissed
    }

    // Check when the user first saw the new design
    const firstSeen = localStorage.getItem('newDesignFirstSeen')
    if (!firstSeen) {
      // First time seeing the new design, record timestamp
      localStorage.setItem('newDesignFirstSeen', Date.now().toString())
      return // Don't show on first visit
    }

    // Check if it's been at least 3 days since first seeing the new design
    const firstSeenDate = new Date(parseInt(firstSeen))
    const now = new Date()
    const daysSinceFirstSeen = Math.floor(
      (now.getTime() - firstSeenDate.getTime()) / (1000 * 60 * 60 * 24)
    )

    if (daysSinceFirstSeen >= 3) {
      // Check if we should show the feedback popover based on last dismissal
      const lastDismissed = localStorage.getItem('feedbackPopoverDismissed')
      if (!lastDismissed) {
        // Show after a short delay
        setTimeout(() => {
          showFeedbackPopover.value = true
        }, 2000)
      } else {
        // Check if it's been at least 3 days since last dismissal
        const lastDismissedDate = new Date(parseInt(lastDismissed))
        const daysSinceDismissed = Math.floor(
          (now.getTime() - lastDismissedDate.getTime()) / (1000 * 60 * 60 * 24)
        )

        if (daysSinceDismissed >= 3) {
          // Show after a short delay
          setTimeout(() => {
            showFeedbackPopover.value = true
          }, 2000)
        }
      }
    }
  })

  // Function to handle feedback submission
  const handleFeedback = () => {
    // Show the feedback modal
    showFeedbackModal.value = true
    showFeedbackPopover.value = false
  }

  // Function to dismiss the feedback popover
  const dismissFeedback = () => {
    // Set to permanently dismissed
    localStorage.setItem('feedbackPopoverPermanentlyDismissed', 'true')
    showFeedbackPopover.value = false
  }

  // Handle feedback modal close
  const handleFeedbackClose = () => {
    showFeedbackModal.value = false
  }

  // Handle feedback submission
  const handleFeedbackSubmitted = (feedbackData: any) => {
    showFeedbackModal.value = false
    // Set to permanently dismissed after feedback submission
    localStorage.setItem('feedbackPopoverPermanentlyDismissed', 'true')
  }
</script>

<template>
  <div>
    <!-- Feedback Popover -->
    <div
      v-if="showFeedbackPopover"
      class="fixed bottom-4 right-4 z-50"
    >
      <div class="w-full max-w-lg overflow-hidden rounded-lg bg-white shadow-lg">
        <div class="flex items-center justify-between p-4">
          <h3 class="text-lg font-semibold">{{ t('feedbackTitle') }}</h3>
          <Button
            variant="ghost"
            size="icon"
            class="h-8 w-8"
            @click="showFeedbackPopover = false"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div class="px-4">
          <p class="text-gray-600">{{ t('feedbackDescription') }}</p>
        </div>
        <div class="flex justify-start gap-3 p-4">
          <Button
            variant="default"
            @click="handleFeedback"
          >
            {{ t('feedbackButton') }}
          </Button>
          <Button
            variant="secondary"
            @click="dismissFeedback"
          >
            {{ t('dismissButton') }}
          </Button>
        </div>
      </div>
    </div>
    <!-- Feedback Modal -->
    <FeedbackModal
      v-if="showFeedbackModal"
      title="Was könnten wir am neuen Design verbessern?"
      description="Bitte teilen Sie uns Ihr Feedback mit. Dies hilft uns sicherzustellen, dass das neue Design allen Ihren Anforderungen entspricht."
      :feature="FeatureEnum.NEW_CASE_PAGE"
      :showThumbsRating="false"
      @close="handleFeedbackClose"
      @submitted="handleFeedbackSubmitted"
    />
  </div>
</template>
